import React from 'react';
import { Avatar, Typography, theme } from 'antd';
import { IconSettings, IconLogout, IconShieldCog, IconChevronRight, IconLanguage, IconHeart } from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import AppLayout from '../../components/Layouts/Layout';
import LanguageSwitcher from '../../components/LanguageSwitcher';
import { useAuth } from '../../contextProviders/AuthProvider';
import { useUserProfile } from '../../contextProviders/UserProfileProvider';
import { getInitials } from '../../helpers/avatarHelper';
import { TRANSLATIONS } from '../../constants/translations';
import { translate } from '../../helpers/translationHelper';

const { Title, Text } = Typography;

interface ProfileMenuRowProps {
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
  danger?: boolean;
}

/**
 * Mobile "Perfil" tab's own page (see MobileBottomNav) -- the destination
 * for everything that used to live in AppHeader's mobile-only Drawer
 * (Settings, language, logout) plus the admin/moderator shortcut that
 * previously sat in the header row as its own icon. Desktop/tablet keep
 * reaching all of these through AppHeader/UserProfile instead, unchanged.
 */
const ProfileMobile: React.FC = () => {
  const { t } = useTranslation();
  const { token } = theme.useToken();
  const navigate = useNavigate();
  const { userName, isAdminOrModerator, logout } = useAuth();
  const { userProfile } = useUserProfile();
  const displayName = userProfile?.displayName || userName || '';
  const photoUrl = userProfile?.photoUrl;

  const rowStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    width: '100%',
    padding: '14px 4px',
    border: 'none',
    background: 'none',
    textAlign: 'left',
    cursor: 'pointer',
    fontSize: 15,
    borderBottom: `1px solid ${token.colorBorderSecondary}`,
  };

  const ProfileMenuRow: React.FC<ProfileMenuRowProps> = ({ icon, label, onClick, danger }) => (
    <button type="button" onClick={onClick} style={{ ...rowStyle, color: danger ? token.colorError : token.colorText }}>
      {icon}
      <span style={{ flex: 1 }}>{label}</span>
      <IconChevronRight size={16} color={token.colorTextTertiary} />
    </button>
  );

  return (
    <AppLayout hideFooter>
      <div style={{ width: '100%', maxWidth: 480 }}>
        <Title level={4} style={{ marginBottom: 20 }}>
          {translate(t, TRANSLATIONS.PROFILE_PAGE_TITLE)}
        </Title>

        <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 24 }}>
          <Avatar src={photoUrl || undefined} size={56} style={photoUrl ? {} : { backgroundColor: token.colorPrimary }}>
            {!photoUrl && getInitials(displayName)}
          </Avatar>
          <Text strong style={{ fontSize: 16 }}>
            {displayName}
          </Text>
        </div>

        <ProfileMenuRow
          icon={<IconHeart size={20} />}
          label={translate(t, TRANSLATIONS.PROFILE_MENU_FAVORITES_LABEL)}
          onClick={() => navigate('/heavy-duty-machinery/favorites')}
        />

        <ProfileMenuRow
          icon={<IconSettings size={20} />}
          label={translate(t, TRANSLATIONS.SETTINGS)}
          onClick={() => navigate('/settings')}
        />

        {isAdminOrModerator && (
          <ProfileMenuRow
            icon={<IconShieldCog size={20} />}
            label={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_NAV_ADMIN_BUTTON)}
            onClick={() => navigate('/admin')}
          />
        )}

        <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '14px 4px', borderBottom: `1px solid ${token.colorBorderSecondary}` }}>
          <IconLanguage size={20} />
          <span style={{ flex: 1 }}>{translate(t, TRANSLATIONS.PROFILE_MENU_LANGUAGE_LABEL)}</span>
          <LanguageSwitcher />
        </div>

        <ProfileMenuRow
          icon={<IconLogout size={20} />}
          label={translate(t, TRANSLATIONS.LOGOUT)}
          onClick={logout}
          danger
        />
      </div>
    </AppLayout>
  );
};

export default ProfileMobile;
